import React, { useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useLanguage } from 'utils/useLanguage';
import useNotify from 'utils/useNotify';

import T from 'utils/useLocalizer';
import NotificationSenderForm from './NotificationSenderForm';
import { useGetOrderSettings, useUpdateOrderSettings } from 'services/orders/ordersServices';
import { IOrderCustomStatusFormData, IOrderSettingsFormData, IOrderStatus } from 'models/ordersModel';
import InvoiceTemplateForm from './InvoiceTemplateForm';
import SystemStatusForm from './SystemStatusForm';
import { useGetMediaAndUtilityAndMarketingTemplateLookups, useGetUtilityAndMarketingTemplateLookups } from 'services/whatsAppTemplate/whatsAppTemplateService';
import { useGetWhatsAppNumbersByStatusIds } from 'services/whatsAppNumber/whatsAppNumberService';
import { PHONE_STATUS } from 'models/whatsAppNumberModel';
import CustomStatusForm from './CustomStatusForm';
import { OrderWAPhoneEmptyContext } from './OrderWAPhoneEmptyContext';
import { TEMPLATE_STATUS } from 'models/whatsAppTemplateModel';

const schema: yup.ObjectSchema<IOrderSettingsFormData> = yup.object({
  waPhoneNumberId: yup
    .number()
    .required('orders.order.settings.notification.sender.form.validation.message')
    .min(1, 'orders.order.settings.notification.sender.form.validation.message'),
  invoiceWATemplateId: yup.number(),
  systemStatuses: yup.array().of(
    yup.object({
      statusId: yup.number().required(),
      isNotify: yup.boolean().required(),
      waTemplateId: yup
        .number()
        .nullable()
        .when('isNotify', {
          is: true,
          then: (schema) => schema.required('orders.order.settings.custom.status.notify.template.form.validation.message'),
          otherwise: (schema) => schema.notRequired(),
        }),
    }),
  ),
  customStatuses: yup.array().of(
    yup.object({
      itemId: yup.number().required(),
      statusId: yup.number().required().min(0, 'orders.order.settings.custom.status.id.form.validation.message'),
      name: yup.string(),
      englishName: yup.string().required('orders.order.settings.custom.status.english.name.form.validation.message'),
      arabicName: yup.string().required('orders.order.settings.custom.status.arabic.name.form.validation.message'),
      isNotify: yup.boolean().required(),
      waTemplateId: yup.number().when('isNotify', {
        is: true,
        then: (schema) => schema.required('orders.order.settings.custom.status.notify.template.form.validation.message'),
        otherwise: (schema) => schema.notRequired(),
      }),
      templateName: yup.string().nullable(),
    }),
  ),
});

const OrderSettingsForm = () => {
  const { language } = useLanguage();
  const { notifySuccess, notifyError } = useNotify();
  const { data: orderSettings, isLoading: isLoadingSettings } = useGetOrderSettings(language);
  const { mutateAsync: updateAsync } = useUpdateOrderSettings();
  const { data: waPhoneNumbers, isLoading: isWaPhoneNumbersLoading } = useGetWhatsAppNumbersByStatusIds([PHONE_STATUS.CONNECTED]);

  const [isWAPhoneNumbersEmpty, setIsWAPhoneNumbersEmpty] = useState(false);
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);
  const [systemStatuses, setSystemStatuses] = useState<IOrderStatus[]>([]);
  const [customStatuses, setCustomStatuses] = useState<IOrderCustomStatusFormData[]>([]);

  const methods = useForm<IOrderSettingsFormData>({
    defaultValues: {
      systemStatuses: [],
      customStatuses: [],
    },
    resolver: yupResolver(schema),
    mode: 'onSubmit',
  });

  const {
    handleSubmit,
    reset,
    watch,
    formState: { isSubmitting, isValid },
  } = methods;

  const { data: waNotificationTemplates, isLoading: isLoadingStatusWATemplate } = useGetUtilityAndMarketingTemplateLookups(true, false);
  const availableNotificationTemplate = useMemo(
    () => waNotificationTemplates?.filter((c) => c.statusId === TEMPLATE_STATUS.APPROVED).sort((a, b) => a.name.localeCompare(b.name)),
    [waNotificationTemplates],
  );

  const { data: waInvoiceTemplates, isLoading: isLoadingInvoiceTemplates } = useGetMediaAndUtilityAndMarketingTemplateLookups(true, false);
  const availableInvoiceTemplates = useMemo(
    () => waInvoiceTemplates?.filter((c) => c.statusId === TEMPLATE_STATUS.APPROVED).sort((a, b) => a.name.localeCompare(b.name)),
    [waInvoiceTemplates],
  );

  const resources = {
    buttonSubmit: T('orders.order.settings.button.submit'),
    settingsUpdatedSuccessfully: T('orders.order.settings.update.successfully.message'),
    settingsFailedUpdate: T('orders.order.settings.update.failed.message'),
  };

  useEffect(() => {
    if (!waPhoneNumbers || waPhoneNumbers.length === 0) {
      setIsWAPhoneNumbersEmpty(true);
    } else {
      setIsWAPhoneNumbersEmpty(false);
    }
  }, [waPhoneNumbers]);

  useEffect(() => {
    if (orderSettings) {
      var systemStatusList = orderSettings.statuses?.filter((s) => s.isSystemStatus) ?? [];
      setSystemStatuses(systemStatusList);

      const customStatusList =
        orderSettings.statuses?.filter((item, index, arr) => {
          return (
            arr.findIndex(
              (x) =>
                !x.isSystemStatus &&
                x.englishName?.trim().toLowerCase() === item.englishName?.trim().toLowerCase() &&
                x.arabicName?.trim() === item.arabicName?.trim(),
            ) === index
          );
        }) ?? [];

      const mappedCustomStatuses = customStatusList.map((item, index) => ({
        itemId: index + 1,
        statusId: item.statusId,
        name: language === 'en' ? item.englishName : item.arabicName,
        englishName: item.englishName,
        arabicName: item.arabicName,
        isNotify: item.isNotify,
        waTemplateId: item.waTemplateId ?? null,
        templateName: item.templateName ?? null,
        isUsed: item.isUsed,
      }));
      setCustomStatuses(mappedCustomStatuses);

      reset({
        waPhoneNumberId:
          waPhoneNumbers && waPhoneNumbers?.length > 0
            ? orderSettings.waPhoneNumberId && waPhoneNumbers?.some((number) => number.id === orderSettings.waPhoneNumberId)
              ? orderSettings.waPhoneNumberId
              : Number(waPhoneNumbers[0].id)
            : 0,
        invoiceWATemplateId:
          availableInvoiceTemplates && availableInvoiceTemplates?.length > 0
            ? orderSettings.invoiceWATemplateId && availableInvoiceTemplates?.some((template) => template.id === orderSettings.invoiceWATemplateId)
              ? orderSettings.invoiceWATemplateId
              : -1
            : -1,        systemStatuses: systemStatusList.map((item) => ({
          statusId: item.orderSystemStatusId ?? 0,
          isNotify: isWAPhoneNumbersEmpty ? false : item.isNotify,
          waTemplateId: isWAPhoneNumbersEmpty ? null : Number(item.waTemplateId),
        })),
        customStatuses: customStatusList.map((item, index) => ({
          itemId: index + 1,
          statusId: item.statusId ?? 0,
          name: language === 'en' ? item.englishName : item.arabicName,
          englishName: item.englishName,
          arabicName: item.arabicName,
          isNotify: isWAPhoneNumbersEmpty ? false : item.isNotify,
          waTemplateId: isWAPhoneNumbersEmpty ? null : Number(item.waTemplateId),
          templateName: item.templateName ?? null,
          isUsed: item.isUsed ?? false,
        })),
      });
    }
  }, [availableInvoiceTemplates, isWAPhoneNumbersEmpty, language, orderSettings, reset, waPhoneNumbers]);

  const onSubmit = (inputs: IOrderSettingsFormData) => {
    setIsFormSubmitting(true);
    const customStatusList = watch('customStatuses') ?? [];
    const transformedStatuses = customStatusList.map((status) => ({
      ...status,
      names: JSON.stringify([
        { l: 'en', n: status.englishName },
        { l: 'ar', n: status.arabicName },
      ]),
    }));

    const payload = {
      ...inputs,
      customStatuses: transformedStatuses,
    };
    updateAsync({ orderSettings: payload })
      .then(() => {
        notifySuccess(resources.settingsUpdatedSuccessfully);
        setIsFormSubmitting(false);
      })
      .catch(() => {
        notifyError(resources.settingsFailedUpdate, { position: 'bottom-right', hideProgressBar: true });
        setIsFormSubmitting(false);
      });
  };

  return (
    <OrderWAPhoneEmptyContext.Provider value={isWAPhoneNumbersEmpty}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="">
            <div className="d-flex flex-column gap-3">
              <NotificationSenderForm waPhoneNumbers={waPhoneNumbers} isLoading={isWaPhoneNumbersLoading} />

              <SystemStatusForm
                statuses={systemStatuses}
                waNotificationTemplates={availableNotificationTemplate ?? []}
                isLoadingTemplates={isLoadingStatusWATemplate}
                isLoadingStatuses={isLoadingSettings}
              />

              <CustomStatusForm customStatusList={customStatuses} isListLoading={isLoadingSettings} />

              <InvoiceTemplateForm templateList={availableInvoiceTemplates ?? []} isLoadingTemplates={isLoadingInvoiceTemplates} />
            </div>
          </div>

          <div className="d-flex justify-content-end my-4">
            <button type="submit" className="btn btn-primary" disabled={isLoadingSettings || isSubmitting || isFormSubmitting || !isValid}>
              <span>{resources.buttonSubmit}</span>
              {(isSubmitting || isFormSubmitting) && (
                <div className="spinner-border spinner-border-sm ms-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              )}
            </button>
          </div>
        </form>
      </FormProvider>
    </OrderWAPhoneEmptyContext.Provider>
  );
};

export default OrderSettingsForm;
